<template>
  <header class="sticky top-0 z-50 bg-white border-b border-neutral-100 shadow-sm">
    <nav class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-18 py-4">

        <!-- Logo -->
        <router-link to="/" class="flex items-center gap-2 flex-shrink-0">
          <img src="@/assets/logo.png" alt="Estate Rwanda" class="h-10 w-auto" />
        </router-link>

        <!-- Desktop Navigation -->
        <div class="hidden md:flex items-center gap-1">
          <router-link
            to="/"
            class="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-primary-500 transition-colors rounded-md hover:bg-primary-50"
            active-class="text-primary-500 bg-primary-50"
            exact
          >Home</router-link>
          <router-link
            to="/properties"
            class="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-primary-500 transition-colors rounded-md hover:bg-primary-50"
            active-class="text-primary-500 bg-primary-50"
          >Properties</router-link>
          <router-link
            to="/about"
            class="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-primary-500 transition-colors rounded-md hover:bg-primary-50"
            active-class="text-primary-500 bg-primary-50"
          >About</router-link>
          <router-link
            to="/blogs"
            class="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-primary-500 transition-colors rounded-md hover:bg-primary-50"
            active-class="text-primary-500 bg-primary-50"
          >Blog</router-link>
          <router-link
            to="/contact"
            class="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-primary-500 transition-colors rounded-md hover:bg-primary-50"
            active-class="text-primary-500 bg-primary-50"
          >Contact</router-link>
        </div>

        <!-- Desktop Search -->
        <div class="hidden md:flex items-center">
          <div class="relative">
            <input
              ref="desktopInput"
              v-model="searchQuery"
              type="text"
              placeholder="Search properties..."
              autocomplete="off"
              class="w-52 lg:w-64 pl-9 pr-4 py-2 text-sm bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent focus:bg-white transition-all duration-200"
              @focus="openDropdown"
              @blur="scheduleClose"
              @keydown.enter.prevent="submitFromInput"
              @keydown.down.prevent="moveDown"
              @keydown.up.prevent="moveUp"
              @keydown.esc="closeDropdown"
            />
            <svg class="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>

            <!-- Desktop Dropdown -->
            <div
              v-if="dropdownOpen && visibleSuggestions.length"
              class="absolute top-full left-0 mt-2 w-72 bg-white border border-neutral-200 rounded-2xl shadow-2xl overflow-hidden z-50"
            >
              <!-- Recent Searches -->
              <div v-if="filteredRecent.length">
                <p class="px-4 pt-3 pb-1 text-[10px] font-bold text-neutral-400 uppercase tracking-widest">Recent</p>
                <div
                  v-for="(item, i) in filteredRecent"
                  :key="'r' + i"
                  class="flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors group"
                  :class="highlightedIndex === i ? 'bg-primary-50' : 'hover:bg-neutral-50'"
                  @mousedown.prevent="selectSuggestion(item)"
                >
                  <svg class="w-4 h-4 text-neutral-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span class="flex-1 text-sm text-neutral-700 truncate">{{ item }}</span>
                  <button
                    class="opacity-0 group-hover:opacity-100 w-5 h-5 rounded-full hover:bg-neutral-200 flex items-center justify-center transition-all flex-shrink-0"
                    @mousedown.prevent.stop="deleteRecent(item)"
                    title="Remove"
                  >
                    <svg class="w-3 h-3 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Popular Searches -->
              <div v-if="filteredPopular.length">
                <p class="px-4 pt-3 pb-1 text-[10px] font-bold text-neutral-400 uppercase tracking-widest" :class="filteredRecent.length ? 'border-t border-neutral-100' : ''">Popular</p>
                <div
                  v-for="(item, i) in filteredPopular"
                  :key="'p' + i"
                  class="flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors"
                  :class="highlightedIndex === filteredRecent.length + i ? 'bg-primary-50' : 'hover:bg-neutral-50'"
                  @mousedown.prevent="selectSuggestion(item)"
                >
                  <svg class="w-4 h-4 text-neutral-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span class="flex-1 text-sm text-neutral-700 truncate">{{ item }}</span>
                </div>
              </div>

              <!-- Fallback: search for typed query -->
              <div
                v-if="searchQuery.trim() && !filteredRecent.includes(searchQuery.trim())"
                class="border-t border-neutral-100"
                :class="highlightedIndex === filteredRecent.length + filteredPopular.length ? 'bg-primary-50' : 'hover:bg-neutral-50'"
                @mousedown.prevent="selectSuggestion(searchQuery.trim())"
              >
                <div class="flex items-center gap-3 px-4 py-2.5 cursor-pointer pb-3">
                  <svg class="w-4 h-4 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span class="text-sm text-neutral-700">Search for <span class="font-semibold text-primary-600">"{{ searchQuery.trim() }}"</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop Auth Buttons -->
        <div class="hidden md:flex items-center gap-3">
          <template v-if="isAuthenticated">
            <router-link
              :to="isAdmin ? '/admin' : '/agent'"
              class="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-primary-500 transition-colors"
            >Dashboard</router-link>
            <button
              @click="handleLogout"
              class="px-5 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors shadow-sm"
            >Logout</button>
          </template>
          <template v-else>
            <router-link
              to="/signin"
              class="px-4 py-2 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors"
            >Sign In</router-link>
            <router-link
              to="/signup"
              class="px-5 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors shadow-sm"
            >Sign Up</router-link>
          </template>
        </div>

        <!-- Mobile Menu Toggle -->
        <button
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="md:hidden p-2 rounded-md text-neutral-600 hover:text-primary-500 hover:bg-primary-50 transition-colors"
          aria-label="Toggle menu"
        >
          <svg v-if="!mobileMenuOpen" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <svg v-else class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

      </div>

      <!-- Mobile Menu -->
      <div v-if="mobileMenuOpen" class="md:hidden border-t border-neutral-100 py-3 space-y-1">

        <!-- Mobile Search -->
        <div class="px-1 pb-1 relative">
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search properties..."
              autocomplete="off"
              class="w-full pl-9 pr-4 py-2.5 text-sm bg-neutral-50 border border-neutral-200 rounded-xl text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              @focus="openDropdown"
              @blur="scheduleClose"
              @keydown.enter.prevent="submitFromInput"
              @keydown.down.prevent="moveDown"
              @keydown.up.prevent="moveUp"
              @keydown.esc="closeDropdown"
            />
            <svg class="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <!-- Mobile Dropdown -->
          <div
            v-if="dropdownOpen && visibleSuggestions.length"
            class="absolute top-full left-1 right-1 mt-1 bg-white border border-neutral-200 rounded-2xl shadow-2xl overflow-hidden z-50"
          >
            <div v-if="filteredRecent.length">
              <p class="px-4 pt-3 pb-1 text-[10px] font-bold text-neutral-400 uppercase tracking-widest">Recent</p>
              <div
                v-for="(item, i) in filteredRecent"
                :key="'mr' + i"
                class="flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors group"
                :class="highlightedIndex === i ? 'bg-primary-50' : 'hover:bg-neutral-50'"
                @mousedown.prevent="selectSuggestion(item)"
              >
                <svg class="w-4 h-4 text-neutral-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span class="flex-1 text-sm text-neutral-700 truncate">{{ item }}</span>
                <button
                  class="opacity-0 group-hover:opacity-100 w-5 h-5 rounded-full hover:bg-neutral-200 flex items-center justify-center transition-all flex-shrink-0"
                  @mousedown.prevent.stop="deleteRecent(item)"
                >
                  <svg class="w-3 h-3 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div v-if="filteredPopular.length">
              <p class="px-4 pt-3 pb-1 text-[10px] font-bold text-neutral-400 uppercase tracking-widest" :class="filteredRecent.length ? 'border-t border-neutral-100' : ''">Popular</p>
              <div
                v-for="(item, i) in filteredPopular"
                :key="'mp' + i"
                class="flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors"
                :class="highlightedIndex === filteredRecent.length + i ? 'bg-primary-50' : 'hover:bg-neutral-50'"
                @mousedown.prevent="selectSuggestion(item)"
              >
                <svg class="w-4 h-4 text-neutral-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span class="flex-1 text-sm text-neutral-700 truncate">{{ item }}</span>
              </div>
            </div>
            <div
              v-if="searchQuery.trim() && !filteredRecent.includes(searchQuery.trim())"
              class="border-t border-neutral-100"
              @mousedown.prevent="selectSuggestion(searchQuery.trim())"
            >
              <div class="flex items-center gap-3 px-4 py-2.5 cursor-pointer pb-3 hover:bg-neutral-50">
                <svg class="w-4 h-4 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span class="text-sm text-neutral-700">Search for <span class="font-semibold text-primary-600">"{{ searchQuery.trim() }}"</span></span>
              </div>
            </div>
          </div>
        </div>

        <router-link to="/" class="block px-4 py-2.5 text-sm font-medium text-neutral-700 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors" @click="mobileMenuOpen = false" exact>Home</router-link>
        <router-link to="/properties" class="block px-4 py-2.5 text-sm font-medium text-neutral-700 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors" @click="mobileMenuOpen = false">Properties</router-link>
        <router-link to="/about" class="block px-4 py-2.5 text-sm font-medium text-neutral-700 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors" @click="mobileMenuOpen = false">About</router-link>
        <router-link to="/blogs" class="block px-4 py-2.5 text-sm font-medium text-neutral-700 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors" @click="mobileMenuOpen = false">Blog</router-link>
        <router-link to="/contact" class="block px-4 py-2.5 text-sm font-medium text-neutral-700 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors" @click="mobileMenuOpen = false">Contact</router-link>

        <div class="pt-3 mt-2 border-t border-neutral-100 flex flex-col gap-2 px-1">
          <template v-if="isAuthenticated">
            <router-link :to="isAdmin ? '/admin' : '/agent'" class="block px-4 py-2.5 text-sm font-medium text-neutral-700 hover:text-primary-500 text-center border border-neutral-300 rounded-lg transition-colors" @click="mobileMenuOpen = false">Dashboard</router-link>
            <button @click="handleLogout" class="block w-full px-4 py-2.5 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 text-center rounded-lg transition-colors">Logout</button>
          </template>
          <template v-else>
            <router-link to="/signin" class="block px-4 py-2.5 text-sm font-medium text-primary-500 hover:text-primary-600 text-center border border-primary-500 rounded-lg transition-colors" @click="mobileMenuOpen = false">Sign In</router-link>
            <router-link to="/signup" class="block px-4 py-2.5 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 text-center rounded-lg transition-colors" @click="mobileMenuOpen = false">Sign Up</router-link>
          </template>
        </div>
      </div>
    </nav>
  </header>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'

const STORAGE_KEY = 'er_recent_searches'
const MAX_RECENT = 8

const POPULAR_SEARCHES = [
  'Apartment for Rent in Kigali',
  'House for Sale',
  'Land for Sale',
  'Studio Apartment',
  'Commercial Space',
  'House for Rent Gasabo',
  'Apartment Nyarutarama',
  'Plot for Sale Kigali',
  'Short Stay Apartment',
  'Villa for Sale'
]

@Options({})
export default class Header extends Vue {
  mobileMenuOpen = false
  searchQuery = ''
  dropdownOpen = false
  highlightedIndex = -1
  recentSearches: string[] = []
  closeTimer: ReturnType<typeof setTimeout> | null = null

  created () {
    this.loadRecent()
  }

  // ── Computed ──────────────────────────────────────────────────────────────

  get filteredRecent (): string[] {
    const q = this.searchQuery.trim().toLowerCase()
    if (!q) return this.recentSearches.slice(0, 5)
    return this.recentSearches.filter(s => s.toLowerCase().includes(q)).slice(0, 5)
  }

  get filteredPopular (): string[] {
    const q = this.searchQuery.trim().toLowerCase()
    const recentSet = new Set(this.recentSearches.map(s => s.toLowerCase()))
    const pool = POPULAR_SEARCHES.filter(s => !recentSet.has(s.toLowerCase()))
    if (!q) return pool.slice(0, 5)
    return pool.filter(s => s.toLowerCase().includes(q)).slice(0, 4)
  }

  get visibleSuggestions (): string[] {
    const all = [...this.filteredRecent, ...this.filteredPopular]
    if (this.searchQuery.trim() && !this.filteredRecent.includes(this.searchQuery.trim())) {
      all.push(this.searchQuery.trim())
    }
    return all
  }

  // ── Dropdown control ─────────────────────────────────────────────────────

  openDropdown () {
    if (this.closeTimer) clearTimeout(this.closeTimer)
    this.dropdownOpen = true
    this.highlightedIndex = -1
  }

  scheduleClose () {
    // Small delay so mousedown on suggestions fires before blur hides them
    this.closeTimer = setTimeout(() => { this.dropdownOpen = false }, 150)
  }

  closeDropdown () {
    this.dropdownOpen = false
    this.highlightedIndex = -1
  }

  // ── Keyboard navigation ──────────────────────────────────────────────────

  moveDown () {
    this.dropdownOpen = true
    this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.visibleSuggestions.length - 1)
  }

  moveUp () {
    this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1)
  }

  submitFromInput () {
    if (this.highlightedIndex >= 0 && this.visibleSuggestions[this.highlightedIndex]) {
      this.selectSuggestion(this.visibleSuggestions[this.highlightedIndex])
    } else {
      const q = this.searchQuery.trim()
      if (q) this.selectSuggestion(q)
    }
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  selectSuggestion (q: string) {
    if (!q) return
    this.saveRecent(q)
    this.searchQuery = ''
    this.dropdownOpen = false
    this.highlightedIndex = -1
    this.mobileMenuOpen = false
    this.$router.push({ path: '/properties', query: { search: q } })
  }

  deleteRecent (item: string) {
    this.recentSearches = this.recentSearches.filter(s => s !== item)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.recentSearches))
  }

  // ── localStorage ─────────────────────────────────────────────────────────

  loadRecent () {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      this.recentSearches = raw ? JSON.parse(raw) : []
    } catch {
      this.recentSearches = []
    }
  }

  saveRecent (q: string) {
    const trimmed = q.trim()
    if (!trimmed) return
    this.recentSearches = [trimmed, ...this.recentSearches.filter(s => s !== trimmed)].slice(0, MAX_RECENT)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.recentSearches))
  }

  // ── Auth ──────────────────────────────────────────────────────────────────

  get isAuthenticated (): boolean {
    return this.$store.getters['auth/isAuthenticated']
  }

  get isAdmin (): boolean {
    return this.$store.getters['auth/isAdmin']
  }

  async handleLogout (): Promise<void> {
    await this.$store.dispatch('auth/logout')
    this.mobileMenuOpen = false
    this.$router.push('/signin')
  }
}
</script>
